import { formatShortMonthDayYear } from "@clipboard-health/date-time";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import { IconButton, Paper, Stack } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";

import { PricingType, VISIT_TYPE_LABELS, VisitType } from "../../types";
import { type VisitDraft } from "./visitDraft";

interface Props {
  draft: VisitDraft;
  onEdit: () => void;
  onRemove: () => void;
}

const iconSx = { fontSize: 18, color: "text.secondary", flexShrink: 0 } as const;

/** Summarizes a not-yet-posted visit on the care-team review screen. */
export function MobileVisitDraftCard({ draft, onEdit, onRemove }: Props) {
  const visitType = draft.visitType as VisitType;
  const isRegularVisit = visitType === VisitType.REGULAR;
  const isHourly = draft.pricingType === PricingType.PER_HOUR;
  const rateSuffix = isHourly ? " per hour" : " per visit";

  const title = [VISIT_TYPE_LABELS[visitType], draft.workerRequirement].filter(Boolean).join(" · ");

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        bgcolor: "background.paper",
        boxShadow: "0 1px 4px 0 rgba(0,0,0,0.08)",
        padding: "1rem",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text variant="h6">{title}</Text>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton aria-label={`Edit ${title} visit`} size="small" onClick={onEdit}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label={`Remove ${title} visit`}
              size="small"
              color="error"
              onClick={onRemove}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        {isRegularVisit && draft.visitsPerWeek && draft.totalWeeks && (
          <Stack direction="row" spacing={1} alignItems="center">
            <RadioButtonUncheckedIcon sx={iconSx} />
            <Text variant="body1">
              {draft.visitsPerWeek}/week for {draft.totalWeeks} weeks
            </Text>
          </Stack>
        )}

        {isHourly && draft.estimatedDuration && (
          <Stack direction="row" spacing={1} alignItems="center">
            <AccessTimeOutlinedIcon sx={iconSx} />
            <Text variant="body1">Estimated duration: {draft.estimatedDuration} hours</Text>
          </Stack>
        )}

        {draft.firstVisitDueDate && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarTodayOutlinedIcon sx={iconSx} />
            <Text variant="body1">
              First visit due:{" "}
              {formatShortMonthDayYear(new Date(`${draft.firstVisitDueDate}T12:00:00`))}
            </Text>
          </Stack>
        )}

        {draft.payPerVisit && (
          <Stack direction="row" spacing={1} alignItems="center">
            <MonetizationOnOutlinedIcon sx={iconSx} />
            <Text variant="body1">
              Pay: ${draft.payPerVisit}
              {rateSuffix}
            </Text>
          </Stack>
        )}

        {draft.chargePerVisit && (
          <Stack direction="row" spacing={1} alignItems="center">
            <ReceiptOutlinedIcon sx={iconSx} />
            <Text variant="body1">
              Charge: ${draft.chargePerVisit}
              {rateSuffix}
            </Text>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
